import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import UploadPage from './pages/UploadPage';
import JobListPage from './pages/JobListPage';
import ResultsPage from './pages/ResultsPage';
import '@fontsource/noto-sans-jp/400.css';
import '@fontsource/noto-sans-jp/700.css';
import AnnotationPage from './pages/AnnotationPage';
import AccuracyMonitoringPage from './pages/AccuracyMonitoringPage';
import AccuracyReportPage from './pages/AccuracyReportPage';
import AccuracyReportDetailPage from './pages/AccuracyReportDetailPage';
import AccuracyReportRunPage from './pages/AccuracyReportRunPage';
import AccuracyCountDrilldownPage from './pages/AccuracyCountDrilldownPage';
import AccuracyTuningSessionPage from './pages/AccuracyTuningSessionPage';
import AccuracyTuningDraftPage from './pages/AccuracyTuningDraftPage';
import AccuracyTuningRunDetailPage from './pages/AccuracyTuningRunDetailPage';
import SettingsPage from './pages/SettingsPage';
import ViolationImageViewerPage from './pages/ViolationImageViewerPage';
import GuidelineManagementPage from './pages/GuidelineManagementPage';
import UrlErrorTracebackPage from './pages/UrlErrorTracebackPage';
import LlmUsagePage from './pages/LlmUsagePage';
import LlmUsageDetailPage from './pages/LlmUsageDetailPage';
import PageStatusDashboardPage from './pages/PageStatusDashboardPage';
import ViolationTrendPage from './pages/ViolationTrendPage';
import { registerAndSubscribe, isPushSupported } from './services/pushService';
import { ToastProvider } from './components/toast/ToastProvider';
import './global.css';
import './App.css';

// Component to handle push notification registration
function PushNotificationHandler() {
  const { session, isAuthenticated } = useAuth();

  useEffect(() => {
    // Only register if user is logged in and browser supports push
    if (isAuthenticated && session?.user && isPushSupported()) {
      // Small delay to ensure auth is fully initialized
      const timer = setTimeout(() => {
        const userId = session.user.id || session.user.username || 'unknown';
        registerAndSubscribe(userId)
          .then((success) => {
            if (success) {
              console.log('Push notification subscription successful');
            } else {
              console.log('Push notification subscription failed or permission denied');
            }
          })
          .catch((error) => {
            console.error('Error registering push notification:', error);
          });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [session, isAuthenticated]);

  return null;
}

function AppContent() {
  return (
    <Router>
      <div className="App">
        <PushNotificationHandler />
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <ProtectedRoute requireAuth={false}>
                <LoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/violation-image/:violationId"
            element={<ViolationImageViewerPage />}
          />

          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute requireAuth={true}>
                <Layout>
                  <Routes>
                    <Route path="/" element={<UploadPage />} />
                    <Route path="/upload" element={<UploadPage />} />
                    <Route path="/job-list" element={<JobListPage />} />
                    <Route path="/results" element={<ResultsPage />} />
                    <Route path="/accuracy" element={<AccuracyReportPage />} />
                    <Route path="/accuracy/monitoring" element={<AccuracyMonitoringPage />} />
                    <Route path="/accuracy/reports/:reportId" element={<AccuracyReportDetailPage />} />
                    <Route
                      path="/accuracy/reports/:reportId/tuning-draft"
                      element={<AccuracyTuningDraftPage />}
                    />
                    <Route path="/accuracy/run" element={<AccuracyReportRunPage />} />
                    <Route path="/accuracy/drilldown" element={<AccuracyCountDrilldownPage />} />
                    <Route path="/accuracy/tuning/:sessionId" element={<AccuracyTuningSessionPage />} />
                    <Route
                      path="/accuracy/tuning/:sessionId/runs/:runId"
                      element={<AccuracyTuningRunDetailPage />}
                    />
                    <Route path="/violation/:jobId/:urlId" element={<AnnotationPage />} />
                    <Route path="/url-error/:jobId/:urlId" element={<UrlErrorTracebackPage />} />
                    <Route path="/llm-usage" element={<LlmUsagePage />} />
                    <Route path="/llm-usage/:id" element={<LlmUsageDetailPage />} />
                    <Route path="/status-dashboard" element={<PageStatusDashboardPage />} />
                    <Route path="/violation-trend" element={<ViolationTrendPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/guidelines" element={<GuidelineManagementPage />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
